import request from '../../../util/SuperagentUtils';

/**
 * Counts rows in any paginated report search, without fetching them.
 *
 * Every report search in the app answers with `{ count, content }`, so asking
 * for a single row and reading `count` gives a total for the price of one small
 * request. That is what makes "how many X" answerable across the whole report
 * surface from one function instead of a bespoke aggregate per report — only a
 * handful of these endpoints expose a server-side summary, but all of them
 * paginate.
 *
 * Dates must already be in the API's MM/DD/YYYY form — use `apiDateRange`.
 * This function used to convert them itself, which hid the conversion from
 * hand-written skills that call request modules directly and left them sending
 * ISO to a server that rejects it. Conversion now happens in exactly one place.
 */
export interface ReportCountParams {
  /** MM/DD/YYYY, as produced by `apiDateRange`. */
  startDate?: string;
  /** MM/DD/YYYY, as produced by `apiDateRange`. */
  endDate?: string;
  locationId?: string;
  /** Report-specific filters that identify the subset being counted. */
  fixedParams?: Record<string, string | number>;
}

export const countReportRows = (
  searchEndpoint: string,
  { startDate, endDate, locationId, fixedParams }: ReportCountParams = {},
): Promise<any> => {
  const query: Record<string, string | number> = {
    page: 0,
    size: 1,
    ...(fixedParams ?? {}),
  };

  if (startDate) query.startDate = startDate;
  if (endDate) query.endDate = endDate;
  if (locationId) query.locationId = locationId;

  return request.get(searchEndpoint).query(query);
};
